import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import Highcharts from 'highcharts/highstock';

import { FormatConfig } from '../domain/detailed-chart.domain';
import { QuoteIssueType, QuoteSettingType, QuoteChartSize, QuoteTimeSeriesType } from '../domain';
import { TimeRangeSelector, FrequencySelector, ChartTypeSelector } from '../quote.constants';
import { DateTimeFormatType } from '../../../../services/date/date-formatter.service';
import { LocalStorageService } from '../../../../services/storage/local-storage.service';
import { ChartsConfigService } from '../../../../components/charts/service/charts-config.service';
import { UtilityService } from '../../../../utils/utility.service';
import { DetailedChartConfigService } from './detailed-chart-config.service';
import { TickPositionsService } from './tick-positions.service';
import { DetailedChartFormatterService } from './detailed-chart-formatter.service';

export interface QuoteChartSettingsIssueType {
  [key: string]: QuoteChartSettings;
}

export interface QuoteChartSettings {
  selectFrequency: FrequencySelector;
  selectTimeRange: TimeRangeSelector;
  selectType: ChartTypeSelector;
}

/**
 * Quote Chart Config Service
 *
 * Service configure chart option, extends DetailedChartConfigService
 */
@Injectable({
  providedIn: 'root'
})
export class QuoteChartConfigService extends DetailedChartConfigService {

  private static readonly LOCALE_PREFIX_NEW: string = 'tdWb.markets.quote.';
  private static readonly LOCALE_PREFIX_OLD: string = 'tdWb.';
  private static readonly REMEMBER_QUOTE_CHART_SETTINGS: string = 'rememberQuoteChartSettings';
  private static readonly XAXIS_TICK_LABEL_FORMAT_TEN_YEARS: DateTimeFormatType = 'YEAR';
  private static readonly THREE_YEARS_XAXIS_TICK_INTERVAL: number = 51 ;
  private static readonly FIVE_YEARS_XAXIS_TICK_INTERVAL: number = 52;
  private static readonly TEN_YEARS_XAXIS_TICK_INTERVAL: number = 53;
  private static readonly ONE_MONTH_XAXIS_TICK_INTERVAL: number = 79;

  constructor(private localStorageService: LocalStorageService,
              translateService: TranslateService,
              chartConfigService: ChartsConfigService,
              tickPositionsService: TickPositionsService,
              chartFormatterService: DetailedChartFormatterService
  ) {
    super(translateService, chartConfigService, tickPositionsService, chartFormatterService);
  }

  /**
   * Update five days chart series
   */
  updateFiveDaysChartSeries(seriesIndex: number, series: QuoteTimeSeriesType,
                            chartConfig: Highcharts.Options, isPennyStock: boolean): void {

    const xAxisTickPositioner = (): Array<number> => this.tickPositionsService.calculateDailyTicks(series);
    const formatConfig: FormatConfig = {
      xAxisTickInterval: QuoteChartConfigService.ONE_MONTH_XAXIS_TICK_INTERVAL,
      xAxisTickPositioner,
      xAxisTickLabelFormatKey: this.XAXIS_TICK_LABEL_FORMAT_MONTHLY,
      useStaticIntradayChart: false,
      marketOpenTime: undefined,
      marker: undefined,
      xAxisTickLabelFormatIncludedYear: undefined,
      xAxisOrdinal: undefined
    };
    this.updateChartSeries(seriesIndex, series, chartConfig, formatConfig, isPennyStock, false);
  }

  /**
   * Update three years chart series
   */
  updateThreeYearsChartSeries(seriesIndex: number, series: QuoteTimeSeriesType,
                              chartConfig: Highcharts.Options, isPennyStock: boolean, chartSize: QuoteChartSize ): void {

    const xAxisTickPositioner = (): Array<number> => this.tickPositionsService
      .calculateThreeYearsTicks(series, chartSize === 'compact');
    const formatConfig: FormatConfig = {
      xAxisTickInterval: QuoteChartConfigService.THREE_YEARS_XAXIS_TICK_INTERVAL,
      xAxisTickPositioner,
      xAxisTickLabelFormatKey: QuoteChartConfigService.XAXIS_TICK_LABEL_FORMAT_TEN_YEARS,
      useStaticIntradayChart: false,
      xAxisOrdinal: true,
      xAxisTickLabelFormatIncludedYear: true,
      marketOpenTime: undefined,
      marker: undefined
    };
    this.updateChartSeries(seriesIndex, series, chartConfig, formatConfig, isPennyStock);
  }

  /**
   * Update five years chart series
   */
  updateFiveYearsChartSeries(seriesIndex: number, series: QuoteTimeSeriesType,
                             chartConfig: Highcharts.Options, isPennyStock: boolean): void {

    const xAxisTickPositioner = (): Array<number> => this.tickPositionsService.calculateFiveYearsTicks(series);
    const formatConfig: FormatConfig = {
      xAxisTickInterval: QuoteChartConfigService.FIVE_YEARS_XAXIS_TICK_INTERVAL,
      xAxisTickPositioner,
      xAxisTickLabelFormatKey: QuoteChartConfigService.XAXIS_TICK_LABEL_FORMAT_TEN_YEARS,
      useStaticIntradayChart: false,
      xAxisOrdinal: true,
      xAxisTickLabelFormatIncludedYear: true,
      marketOpenTime: undefined,
      marker: undefined
    };
    this.updateChartSeries(seriesIndex, series, chartConfig, formatConfig, isPennyStock);
  }

  /**
   * Update ten years chart series
   */
  updateTenYearsChartSeries(seriesIndex: number, series: QuoteTimeSeriesType,
                            chartConfig: Highcharts.Options, isPennyStock: boolean, chartSize: QuoteChartSize): void {

    const xAxisTickPositioner = (): Array<number> => this.tickPositionsService
      .calculateTenYearsTicks(series, chartSize === 'compact');
    const formatConfig: FormatConfig = {
      xAxisTickInterval: QuoteChartConfigService.TEN_YEARS_XAXIS_TICK_INTERVAL,
      xAxisTickPositioner,
      xAxisTickLabelFormatKey: QuoteChartConfigService.XAXIS_TICK_LABEL_FORMAT_TEN_YEARS,
      useStaticIntradayChart: false,
      xAxisOrdinal: true,
      marketOpenTime: undefined,
      marker: undefined,
      xAxisTickLabelFormatIncludedYear: undefined
    };
    this.updateChartSeries(seriesIndex, series, chartConfig, formatConfig, isPennyStock);
  }

  /**
   * Update twenty years chart series
   */
  updateTwentyYearsChartSeries(seriesIndex: number, series: QuoteTimeSeriesType,
                               chartConfig: Highcharts.Options, isPennyStock: boolean, chartSize: QuoteChartSize): void {

    const xAxisTickPositioner = (): Array<number> => this.tickPositionsService
      .calculateTwentyYearsTicks(series, chartSize === 'compact');
    const formatConfig: FormatConfig = {
      xAxisTickInterval: QuoteChartConfigService.TEN_YEARS_XAXIS_TICK_INTERVAL,
      xAxisTickPositioner,
      xAxisTickLabelFormatKey: QuoteChartConfigService.XAXIS_TICK_LABEL_FORMAT_TEN_YEARS,
      useStaticIntradayChart: false,
      xAxisOrdinal: true,
      marketOpenTime: undefined,
      marker: undefined,
      xAxisTickLabelFormatIncludedYear: undefined
    };
    this.updateChartSeries(seriesIndex, series, chartConfig, formatConfig, isPennyStock);
  }

  /**
   * Save chart setting to local storage
   */
  setChartSettings(settings: QuoteChartSettings, issueType: QuoteIssueType): void {
    const chartSettings = this.fixLocale(settings, QuoteChartConfigService.LOCALE_PREFIX_NEW, QuoteChartConfigService.LOCALE_PREFIX_OLD);
    let storeSettings: QuoteChartSettingsIssueType = this.localStorageService
      .getItem(QuoteChartConfigService.REMEMBER_QUOTE_CHART_SETTINGS);
    if (storeSettings === undefined || storeSettings === null) {
      storeSettings = {};
    }
    storeSettings[this.convertToSettingType(issueType)] = chartSettings;
    this.localStorageService.setItem(QuoteChartConfigService.REMEMBER_QUOTE_CHART_SETTINGS, storeSettings);
  }

  /**
   * Get chart setting from local storage
   */
  getChartSettings(issueType: QuoteIssueType): QuoteChartSettings {
    const storeSettings: QuoteChartSettingsIssueType = this.localStorageService
      .getItem<QuoteChartSettingsIssueType>(QuoteChartConfigService.REMEMBER_QUOTE_CHART_SETTINGS);
    if (storeSettings !== undefined && storeSettings !== null) {
      const settings = storeSettings[this.convertToSettingType(issueType)];
      return this.fixLocale(settings, QuoteChartConfigService.LOCALE_PREFIX_OLD, QuoteChartConfigService.LOCALE_PREFIX_NEW);
    }
    return undefined;
  }

  /**
   * Map QuotesIssueType to QuotesIssueType
   */
  private convertToSettingType(issueType: QuoteIssueType): QuoteSettingType {
    switch (issueType) {
      case QuoteIssueType.STOCK:
      case QuoteIssueType.ETF:
        return QuoteSettingType.SE;
      case QuoteIssueType.OPTION:
        return QuoteSettingType.OP;
      case QuoteIssueType.INDEX:
        return QuoteSettingType.IN;
      case QuoteIssueType.MF:
        return QuoteSettingType.MF;
      default:
        return QuoteSettingType.SE;
    }
  }

  // Change locale id before saving as it is shared with angularjs
  // TODO: remove after angular migration
  private fixLocale(settings: QuoteChartSettings, from: string, to: string): QuoteChartSettings {
    if (settings !== undefined) {
      const chartSettings = UtilityService.deepClone(settings);
      if (chartSettings.selectFrequency !== undefined && chartSettings.selectFrequency.translationId !== undefined) {
        chartSettings.selectFrequency.translationId = chartSettings.selectFrequency.translationId.replace(from, to);
      }
      if (chartSettings.selectTimeRange !== undefined && chartSettings.selectTimeRange.translationId !== undefined) {
        chartSettings.selectTimeRange.translationId = chartSettings.selectTimeRange.translationId.replace(from, to);
      }
      if (settings.selectType !== undefined && chartSettings.selectType.translationId !== undefined) {
        chartSettings.selectType.translationId = chartSettings.selectType.translationId.replace(from, to);
      }
      return chartSettings;
    }
    return undefined;
  }
}
